/* eslint-disable */
import React, { useRef, useEffect } from 'react';
import { useGLTF, useAnimations } from '@react-three/drei';
import * as THREE from 'three';

export default function ModelViewer({ mouse, triggerRef }) {
  const MODEL_PATH = '/assets/models/robot-companion.glb';
  const group = useRef();
  const { scene, animations } = useGLTF(MODEL_PATH);
  const { actions } = useAnimations(animations, group);
  const idleRef = useRef({ t: 0, rotating: false });

  useEffect(() => {
    if (!triggerRef) return;

    const easeInOut = (t) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const smoothRotate = (from, to, duration, onComplete) => {
      let t = 0;
      const fps = 60;
      const interval = 1000 / fps;
      const anim = setInterval(() => {
        if (!group.current) return;
        t += 1 / (duration * fps);
        const eased = easeInOut(Math.min(t, 1));
        group.current.rotation.y = THREE.MathUtils.lerp(from, to, eased);
        if (t >= 1) {
          clearInterval(anim);
          onComplete?.();
        }
      }, interval);
    };

    triggerRef.current = {
      wave: () => {
        const a = actions?.Wave;
        if (a) {
          a.reset().setLoop(THREE.LoopOnce, 1);
          a.clampWhenFinished = true;
          a.play();
        } else if (group.current) {
          group.current.rotation.y += 0.3;
          setTimeout(() => (group.current.rotation.y -= 0.3), 400);
        }
      },
      blink: () => {
        const a = actions?.Blink;
        if (a) {
          a.reset().setLoop(THREE.LoopOnce, 1);
          a.clampWhenFinished = true;
          a.play();
        } else if (group.current) {
          const prev = group.current.rotation.x;
          group.current.rotation.x = prev + 0.15;
          setTimeout(() => (group.current.rotation.x = prev), 250);
        }
      },
      turnBackThenWave: () => {
        if (!group.current || idleRef.current.rotating) return;
        idleRef.current.rotating = true;
        const start = group.current.rotation.y;
        const end = Math.PI;

        smoothRotate(start, end, 1.8, () => {
          setTimeout(() => {
            smoothRotate(end, 0, 1.8, () => {
              idleRef.current.rotating = false;
              triggerRef.current?.wave?.();
            });
          }, 1200);
        });
      },
    };
  }, [actions, triggerRef]);

  useEffect(() => {
    const animate = (delta) => {
      if (!group.current || idleRef.current.rotating) return;
      const lerp = (a, b, t) => a + (b - a) * t;
      const tx = (mouse.current.x || 0) * 0.25;
      const ty = (mouse.current.y || 0) * -0.25;
      group.current.rotation.x = lerp(group.current.rotation.x, tx, 0.06);
      group.current.rotation.y = lerp(group.current.rotation.y, ty, 0.06);
      idleRef.current.t += delta;
      const bob = Math.sin(idleRef.current.t * 1.5) * 0.05;
      group.current.position.y = lerp(group.current.position.y, bob, 0.08);
    };
  }, [mouse]);

  return (
    <group
      ref={group}
      dispose={null}
      position={[0, -0.2, 0]}
      scale={[1.3, 1.3, 1.3]}
    >
      <primitive object={scene} />
    </group>
  );
}
